import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PageSEO from '@/components/PageSEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calculator,
  ExternalLink,
  Euro,
  Percent,
  Building2,
  Users,
  FileText,
  CreditCard,
  Globe,
  Landmark,
  Receipt,
  Search,
  BookOpen,
  Scale,
  Briefcase,
  PiggyBank,
  UserCheck,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UtilityPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-16" data-testid="utility-page">
      <PageSEO path="/utility" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
            Strumenti Utili
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
            Utility per la tua Azienda
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Calcolatori fiscali e accesso rapido ai principali portali istituzionali
          </p>
        </motion.div>

        <Tabs defaultValue="calcolatori" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="calcolatori" className="flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Calcolatori
            </TabsTrigger>
            <TabsTrigger value="portali" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Portali Utili
            </TabsTrigger>
          </TabsList>

          {/* CALCOLATORI TAB */}
          <TabsContent value="calcolatori">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <VATCalculator />
              <CodiceFiscaleCalculator />
              <ContributiINPSCalculator />
              <RitenutaCalculator />
              <ForfettarioCalculator />
              <INPSCalculator />
              <CostoDipendenteCalculator />
              <NettoLordoCalculator />
            </div>
          </TabsContent>

          {/* PORTALI TAB */}
          <TabsContent value="portali">
            <PortaliUtili />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ============== CODICE FISCALE CALCULATOR ==============
const CodiceFiscaleCalculator = () => {
  const [cognome, setCognome] = useState('');
  const [nome, setNome] = useState('');
  const [dataNascita, setDataNascita] = useState('');
  const [sesso, setSesso] = useState('');
  const [comuneQuery, setComuneQuery] = useState('');
  const [comuneSuggestions, setComuneSuggestions] = useState([]);
  const [selectedComune, setSelectedComune] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const searchComuni = async (query) => {
    setComuneQuery(query);
    setSelectedComune(null);
    if (query.length < 2) { setComuneSuggestions([]); setShowSuggestions(false); return; }
    try {
      const r = await axios.get(`${API}/utilities/comuni/search?q=${encodeURIComponent(query)}&limit=10`);
      setComuneSuggestions(r.data);
      setShowSuggestions(true);
    } catch { setComuneSuggestions([]); }
  };

  const selectComune = (comune) => {
    setSelectedComune(comune);
    setComuneQuery(`${comune.nome} (${comune.sigla}) - ${comune.codice_catastale}`);
    setShowSuggestions(false);
  };

  const calculate = async () => {
    if (!cognome || !nome || !dataNascita || !sesso) { toast.error('Compila tutti i campi'); return; }
    if (!selectedComune) { toast.error('Seleziona un comune dalla lista'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/utilities/codice-fiscale`, {
        cognome, nome, data_nascita: dataNascita, sesso, comune_nascita: selectedComune.codice_catastale
      });
      setResult(r.data);
    } catch { toast.error('Errore nel calcolo'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="codice-fiscale-calculator">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
          <UserCheck className="w-5 h-5 text-teal-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Codice Fiscale</h3>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Cognome</Label>
            <Input value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder="Rossi" className="mt-1" data-testid="cf-cognome" />
          </div>
          <div>
            <Label>Nome</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Mario" className="mt-1" data-testid="cf-nome" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Data di nascita</Label>
            <Input type="date" value={dataNascita} onChange={(e) => setDataNascita(e.target.value)} className="mt-1" data-testid="cf-data" />
          </div>
          <div>
            <Label>Sesso</Label>
            <Select value={sesso} onValueChange={setSesso}>
              <SelectTrigger className="mt-1" data-testid="cf-sesso"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="M">Maschio</SelectItem>
                <SelectItem value="F">Femmina</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="relative">
          <Label>Comune di nascita (cerca tra 7.904 comuni)</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={comuneQuery}
              onChange={(e) => searchComuni(e.target.value)}
              onFocus={() => comuneSuggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Inizia a digitare il nome del comune..."
              className="pl-9"
              data-testid="cf-comune-search"
            />
          </div>
          {showSuggestions && comuneSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {comuneSuggestions.map((c, i) => (
                <button key={i} onMouseDown={() => selectComune(c)}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm flex items-center justify-between"
                  data-testid={`comune-option-${c.codice_catastale}`}>
                  <span><strong>{c.nome}</strong> ({c.sigla}) - {c.provincia}</span>
                  <span className="text-xs text-slate-400 font-mono">{c.codice_catastale}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button onClick={calculate} className="w-full btn-accent" disabled={loading} data-testid="cf-calculate">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
          Calcola Codice Fiscale
        </Button>
        {result && (
          <div className="bg-teal-50 rounded-lg p-4 text-center">
            <p className="text-xs text-teal-600 font-medium mb-1">Il tuo Codice Fiscale</p>
            <p className="text-2xl font-mono font-bold text-teal-900 tracking-widest" data-testid="cf-result">{result.codice_fiscale}</p>
            <div className="grid grid-cols-5 gap-1 mt-3 text-xs text-teal-700">
              <span>{result.dettaglio.cognome}</span><span>{result.dettaglio.nome}</span>
              <span>{result.dettaglio.data_nascita}</span><span>{result.dettaglio.comune}</span>
              <span>{result.dettaglio.carattere_controllo}</span>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500">* Cerca tra tutti i 7.904 comuni italiani del database ISTAT.</p>
      </div>
    </motion.div>
  );
};

// ============== CONTRIBUTI INPS AVANZATO ==============
const ContributiINPSCalculator = () => {
  const [reddito, setReddito] = useState('');
  const [gestione, setGestione] = useState('');
  const [eta, setEta] = useState('35');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    if (!reddito || !gestione) { toast.error('Compila reddito e tipo gestione'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/utilities/contributi`, {
        reddito_annuo: parseFloat(reddito), tipo_gestione: gestione, eta: parseInt(eta)
      });
      setResult(r.data);
    } catch { toast.error('Errore nel calcolo'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="contributi-calculator">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <PiggyBank className="w-5 h-5 text-orange-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Simulatore Contributi INPS</h3>
      </div>
      <div className="space-y-4">
        <div>
          <Label>Reddito annuo (€)</Label>
          <Input type="number" value={reddito} onChange={(e) => setReddito(e.target.value)} placeholder="40000" className="mt-1" data-testid="contrib-reddito" />
        </div>
        <div>
          <Label>Tipo gestione</Label>
          <Select value={gestione} onValueChange={setGestione}>
            <SelectTrigger className="mt-1" data-testid="contrib-gestione"><SelectValue placeholder="Seleziona gestione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="separata">Gestione Separata</SelectItem>
              <SelectItem value="artigiani">Artigiani</SelectItem>
              <SelectItem value="commercianti">Commercianti</SelectItem>
              <SelectItem value="forfettario">Regime Forfettario</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Età</Label>
          <Input type="number" value={eta} onChange={(e) => setEta(e.target.value)} className="mt-1" data-testid="contrib-eta" />
        </div>
        <Button onClick={calculate} className="w-full btn-accent" disabled={loading} data-testid="contrib-calculate">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
          Simula Contributi
        </Button>
        {result && (
          <div className="space-y-3">
            {result.simulazioni.map((sim, i) => (
              <div key={i} className="bg-orange-50 rounded-lg p-4">
                <p className="font-semibold text-slate-900 text-sm mb-2">{sim.tipo}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">Aliquota:</span> <span className="font-medium">{sim.aliquota}%</span></div>
                  <div><span className="text-slate-500">Imponibile:</span> <span className="font-medium">€{sim.reddito_imponibile.toLocaleString('it-IT')}</span></div>
                  <div><span className="text-slate-500">Annuo:</span> <span className="font-bold text-orange-700">€{sim.contributo_annuo.toLocaleString('it-IT')}</span></div>
                  <div><span className="text-slate-500">Trimestrale:</span> <span className="font-medium">€{sim.contributo_trimestrale.toLocaleString('it-IT')}</span></div>
                </div>
                <p className="text-xs text-slate-500 mt-2">{sim.note}</p>
              </div>
            ))}
            <p className="text-xs text-slate-500">{result.disclaimer}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ============== CALCOLATORE IVA ==============
const VATCalculator = () => {
  const [importo, setImporto] = useState('');
  const [aliquota, setAliquota] = useState('22');
  const [tipo, setTipo] = useState('scorporo');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!importo) return;
    const imp = parseFloat(importo);
    const aliq = parseFloat(aliquota);
    
    if (tipo === 'scorporo') {
      const imponibile = imp / (1 + aliq / 100);
      const iva = imp - imponibile;
      setResult({ imponibile, iva, totale: imp });
    } else {
      const iva = imp * aliq / 100;
      setResult({ imponibile: imp, iva, totale: imp + iva });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-200 p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Receipt className="w-5 h-5 text-indigo-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Calcolatore IVA</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Tipo calcolo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scorporo">Scorporo IVA (da lordo)</SelectItem>
              <SelectItem value="aggiunta">Aggiungi IVA (da netto)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Importo (€)</Label>
          <Input
            type="number"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
            placeholder="1000.00"
            className="mt-1"
          />
        </div>

        <div>
          <Label>Aliquota IVA</Label>
          <Select value={aliquota} onValueChange={setAliquota}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="22">22% Ordinaria</SelectItem>
              <SelectItem value="10">10% Ridotta</SelectItem>
              <SelectItem value="5">5% Super ridotta</SelectItem>
              <SelectItem value="4">4% Minima</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={calculate} className="w-full btn-accent">
          Calcola
        </Button>

        {result && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Imponibile</span>
              <span className="font-semibold">€{result.imponibile.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">IVA ({aliquota}%)</span>
              <span className="font-semibold text-indigo-600">€{result.iva.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">Totale</span>
              <span className="font-bold text-lg">€{result.totale.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ============== CALCOLATORE RITENUTA D'ACCONTO ==============
const RitenutaCalculator = () => {
  const [importo, setImporto] = useState('');
  const [aliquota, setAliquota] = useState('20');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!importo) return;
    const imp = parseFloat(importo);
    const aliq = parseFloat(aliquota);
    const ritenuta = imp * aliq / 100;
    setResult({ lordo: imp, ritenuta, netto: imp - ritenuta });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-white rounded-2xl border border-slate-200 p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
          <Percent className="w-5 h-5 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Ritenuta d'Acconto</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Compenso lordo (€)</Label>
          <Input
            type="number"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
            placeholder="1000.00"
            className="mt-1"
          />
        </div>

        <div>
          <Label>Aliquota ritenuta</Label>
          <Select value={aliquota} onValueChange={setAliquota}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20% (Professionisti)</SelectItem>
              <SelectItem value="23">23% (Agenti)</SelectItem>
              <SelectItem value="4">4% (Condomini)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={calculate} className="w-full btn-accent">
          Calcola
        </Button>

        {result && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Compenso lordo</span>
              <span className="font-semibold">€{result.lordo.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Ritenuta ({aliquota}%)</span>
              <span className="font-semibold text-red-600">-€{result.ritenuta.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">Netto da incassare</span>
              <span className="font-bold text-lg text-green-600">€{result.netto.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ============== CALCOLATORE REGIME FORFETTARIO ==============
const ForfettarioCalculator = () => {
  const [fatturato, setFatturato] = useState('');
  const [coefficiente, setCoefficiente] = useState('78');
  const [aliquota, setAliquota] = useState('15');
  const [result, setResult] = useState(null);

  const coefficienti = [
    { value: '40', label: '40% - Commercio ingrosso/dettaglio' },
    { value: '54', label: '54% - Commercio ambulante alimentari' },
    { value: '62', label: '62% - Intermediari commercio' },
    { value: '67', label: '67% - Attività professionali' },
    { value: '78', label: '78% - Servizi, professionisti' },
    { value: '86', label: '86% - Costruzioni, immobiliari' },
  ];

  const calculate = () => {
    if (!fatturato) return;
    const fatt = parseFloat(fatturato);
    const coeff = parseFloat(coefficiente);
    const aliq = parseFloat(aliquota);
    
    const redditoImponibile = fatt * coeff / 100;
    const imposta = redditoImponibile * aliq / 100;
    const contributiINPS = redditoImponibile * 26.07 / 100; // Gestione separata 26.07%
    const nettoStimato = fatt - imposta - contributiINPS;
    
    setResult({ 
      fatturato: fatt, 
      redditoImponibile, 
      imposta, 
      contributiINPS,
      nettoStimato 
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white rounded-2xl border border-slate-200 p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <PiggyBank className="w-5 h-5 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Regime Forfettario</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Fatturato annuo (€)</Label>
          <Input
            type="number"
            value={fatturato}
            onChange={(e) => setFatturato(e.target.value)}
            placeholder="50000.00"
            className="mt-1"
          />
        </div>

        <div>
          <Label>Coefficiente redditività</Label>
          <Select value={coefficiente} onValueChange={setCoefficiente}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {coefficienti.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Aliquota imposta</Label>
          <Select value={aliquota} onValueChange={setAliquota}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5% (primi 5 anni nuova attività)</SelectItem>
              <SelectItem value="15">15% (ordinaria)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={calculate} className="w-full btn-accent">
          Calcola
        </Button>

        {result && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Fatturato</span>
              <span className="font-semibold">€{result.fatturato.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Reddito imponibile ({coefficiente}%)</span>
              <span className="font-semibold">€{result.redditoImponibile.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Imposta sostitutiva ({aliquota}%)</span>
              <span className="font-semibold text-red-600">-€{result.imposta.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Contributi INPS (26.07%)</span>
              <span className="font-semibold text-red-600">-€{result.contributiINPS.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">Netto stimato</span>
              <span className="font-bold text-lg text-green-600">€{result.nettoStimato.toFixed(2)}</span>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500">* Calcolo indicativo. I contributi INPS variano in base alla gestione previdenziale.</p>
      </div>
    </motion.div>
  );
};

// ============== CALCOLATORE CONTRIBUTI INPS ==============
const INPSCalculator = () => {
  const [reddito, setReddito] = useState('');
  const [gestione, setGestione] = useState('separata');
  const [result, setResult] = useState(null);

  const aliquote = {
    separata: { aliquota: 26.07, nome: 'Gestione Separata' },
    artigiani: { aliquota: 24.00, nome: 'Artigiani' },
    commercianti: { aliquota: 24.48, nome: 'Commercianti' },
  };

  const calculate = () => {
    if (!reddito) return;
    const redd = parseFloat(reddito);
    const { aliquota, nome } = aliquote[gestione];
    const contributi = redd * aliquota / 100;
    
    setResult({ reddito: redd, contributi, aliquota, gestione: nome });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white rounded-2xl border border-slate-200 p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Landmark className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Contributi INPS</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Reddito imponibile (€)</Label>
          <Input
            type="number"
            value={reddito}
            onChange={(e) => setReddito(e.target.value)}
            placeholder="30000.00"
            className="mt-1"
          />
        </div>

        <div>
          <Label>Gestione previdenziale</Label>
          <Select value={gestione} onValueChange={setGestione}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="separata">Gestione Separata (26.07%)</SelectItem>
              <SelectItem value="artigiani">Artigiani (24.00%)</SelectItem>
              <SelectItem value="commercianti">Commercianti (24.48%)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={calculate} className="w-full btn-accent">
          Calcola
        </Button>

        {result && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Reddito imponibile</span>
              <span className="font-semibold">€{result.reddito.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">{result.gestione} ({result.aliquota}%)</span>
              <span className="font-semibold text-red-600">€{result.contributi.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">Contributi annui</span>
              <span className="font-bold text-lg">€{result.contributi.toFixed(2)}</span>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500">* Aliquote 2025. Potrebbero esserci minimali e massimali contributivi.</p>
      </div>
    </motion.div>
  );
};

// ============== CALCOLATORE COSTO DIPENDENTE ==============
const CostoDipendenteCalculator = () => {
  const [ralAnnua, setRalAnnua] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!ralAnnua) return;
    const ral = parseFloat(ralAnnua);
    
    // Contributi INPS carico azienda circa 30%
    const contributiAzienda = ral * 0.30;
    // TFR circa 7.4%
    const tfr = ral * 0.074;
    // INAIL circa 0.5%
    const inail = ral * 0.005;
    
    const costoTotale = ral + contributiAzienda + tfr + inail;
    const costoMensile = costoTotale / 12;
    
    setResult({ ral, contributiAzienda, tfr, inail, costoTotale, costoMensile });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="bg-white rounded-2xl border border-slate-200 p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Users className="w-5 h-5 text-purple-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Costo Dipendente</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label>RAL - Retribuzione Annua Lorda (€)</Label>
          <Input
            type="number"
            value={ralAnnua}
            onChange={(e) => setRalAnnua(e.target.value)}
            placeholder="30000.00"
            className="mt-1"
          />
        </div>

        <Button onClick={calculate} className="w-full btn-accent">
          Calcola costo azienda
        </Button>

        {result && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">RAL dipendente</span>
              <span className="font-semibold">€{result.ral.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Contributi INPS (~30%)</span>
              <span className="font-semibold">€{result.contributiAzienda.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">TFR (~7.4%)</span>
              <span className="font-semibold">€{result.tfr.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">INAIL (~0.5%)</span>
              <span className="font-semibold">€{result.inail.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">Costo annuo azienda</span>
              <span className="font-bold text-lg">€{result.costoTotale.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Costo mensile</span>
              <span className="font-bold text-indigo-600">€{result.costoMensile.toFixed(2)}</span>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500">* Stima indicativa. Il costo effettivo varia in base a CCNL, settore e altri fattori.</p>
      </div>
    </motion.div>
  );
};

// ============== CALCOLATORE NETTO/LORDO ==============
const NettoLordoCalculator = () => {
  const [importo, setImporto] = useState('');
  const [tipo, setTipo] = useState('lordo');
  const [aliquotaMedia, setAliquotaMedia] = useState('30');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!importo) return;
    const imp = parseFloat(importo);
    const aliq = parseFloat(aliquotaMedia);
    
    if (tipo === 'lordo') {
      const netto = imp * (1 - aliq / 100);
      setResult({ lordo: imp, netto, trattenute: imp - netto });
    } else {
      const lordo = imp / (1 - aliq / 100);
      setResult({ lordo, netto: imp, trattenute: lordo - imp });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="bg-white rounded-2xl border border-slate-200 p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
          <Euro className="w-5 h-5 text-rose-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Da Lordo a Netto</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Tipo calcolo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lordo">Da lordo a netto</SelectItem>
              <SelectItem value="netto">Da netto a lordo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Importo (€)</Label>
          <Input
            type="number"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
            placeholder="2000.00"
            className="mt-1"
          />
        </div>

        <div>
          <Label>Aliquota media trattenute</Label>
          <Select value={aliquotaMedia} onValueChange={setAliquotaMedia}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25% (redditi bassi)</SelectItem>
              <SelectItem value="30">30% (redditi medi)</SelectItem>
              <SelectItem value="35">35% (redditi medio-alti)</SelectItem>
              <SelectItem value="40">40% (redditi alti)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={calculate} className="w-full btn-accent">
          Calcola
        </Button>

        {result && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Lordo</span>
              <span className="font-semibold">€{result.lordo.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Trattenute ({aliquotaMedia}%)</span>
              <span className="font-semibold text-red-600">-€{result.trattenute.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">Netto</span>
              <span className="font-bold text-lg text-green-600">€{result.netto.toFixed(2)}</span>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500">* Stima indicativa. Le trattenute effettive dipendono da IRPEF, addizionali, detrazioni.</p>
      </div>
    </motion.div>
  );
};

// ============== PORTALI UTILI ==============
const PortaliUtili = () => {
  const portali = [
    {
      categoria: 'Agenzia delle Entrate',
      items: [
        {
          nome: 'Cassetto Fiscale',
          descrizione: 'Accedi alle tue dichiarazioni e dati fiscali',
          url: 'https://telematici.agenziaentrate.gov.it/Main/index.jsp',
          icon: FileText,
          color: 'indigo'
        },
        {
          nome: 'Fatture e Corrispettivi',
          descrizione: 'Portale per fatturazione elettronica',
          url: 'https://ivaservizi.agenziaentrate.gov.it/portale/',
          icon: Receipt,
          color: 'indigo'
        },
        {
          nome: 'Agenzia Entrate',
          descrizione: 'Sito ufficiale dell\'Agenzia delle Entrate',
          url: 'https://www.agenziaentrate.gov.it',
          icon: Landmark,
          color: 'indigo'
        },
      ]
    },
    {
      categoria: 'Previdenza e Lavoro',
      items: [
        {
          nome: 'INPS Online',
          descrizione: 'Servizi previdenziali e contributivi',
          url: 'https://www.inps.it/',
          icon: Users,
          color: 'blue'
        },
        {
          nome: 'INAIL',
          descrizione: 'Assicurazione infortuni sul lavoro',
          url: 'https://www.inail.it/',
          icon: Scale,
          color: 'blue'
        },
        {
          nome: 'Cassa Commercialisti',
          descrizione: 'Cassa previdenza dottori commercialisti',
          url: 'https://www.cnpadc.it/',
          icon: Briefcase,
          color: 'blue'
        },
      ]
    },
    {
      categoria: 'Camera di Commercio',
      items: [
        {
          nome: 'Registro Imprese',
          descrizione: 'Visure camerali e informazioni aziende',
          url: 'https://www.registroimprese.it/',
          icon: Building2,
          color: 'green'
        },
        {
          nome: 'Telemaco',
          descrizione: 'Pratiche camerali online',
          url: 'https://telemaco.infocamere.it/',
          icon: Globe,
          color: 'green'
        },
        {
          nome: 'Codici ATECO',
          descrizione: 'Ricerca codici attività economica',
          url: 'https://www.codiceateco.it/',
          icon: Search,
          color: 'green'
        },
      ]
    },
    {
      categoria: 'Informazione e Aggiornamento',
      items: [
        {
          nome: 'Il Sole 24 Ore',
          descrizione: 'Notizie economiche e fiscali',
          url: 'https://www.ilsole24ore.com/',
          icon: BookOpen,
          color: 'amber'
        },
        {
          nome: 'Fiscoetasse',
          descrizione: 'Approfondimenti fiscali e tributari',
          url: 'https://www.fiscoetasse.com/',
          icon: FileText,
          color: 'amber'
        },
        {
          nome: 'Gazzetta Ufficiale',
          descrizione: 'Normativa e leggi italiane',
          url: 'https://www.gazzettaufficiale.it/',
          icon: Scale,
          color: 'amber'
        },
      ]
    },
    {
      categoria: 'Pagamenti e Tributi',
      items: [
        {
          nome: 'PagoPA',
          descrizione: 'Pagamenti verso la Pubblica Amministrazione',
          url: 'https://www.pagopa.gov.it/',
          icon: CreditCard,
          color: 'purple'
        },
        {
          nome: 'F24 Web',
          descrizione: 'Compilazione e invio modelli F24',
          url: 'https://telematici.agenziaentrate.gov.it/Main/index.jsp',
          icon: Euro,
          color: 'purple'
        },
        {
          nome: 'Agenzia Riscossione',
          descrizione: 'Verifica e paga cartelle esattoriali',
          url: 'https://www.agenziaentrateriscossione.gov.it/',
          icon: Landmark,
          color: 'purple'
        },
      ]
    },
  ];

  const colorClasses = {
    indigo: 'bg-indigo-100 text-indigo-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="space-y-8">
      {portali.map((categoria, idx) => (
        <motion.div
          key={categoria.categoria}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.1 }}
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">{categoria.categoria}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoria.items.map((item) => (
              <a
                key={item.nome}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-indigo-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[item.color]}`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                        {item.nome}
                      </h4>
                      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{item.descrizione}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default UtilityPage;
